<script setup lang="ts">
import { useConsultantKpi } from "~/components/consultant-kpi/composables/useConsultantKpi";
import { useManagees } from "~/components/team-member/composables/useManagees";
import { useUserMe } from "~/components/user/composables/useUser";
import { useFeatureFlag } from "~/composables/useFeatureFlag";
import { AwBadge, AwCheckbox, AwEmptyState, AwSkeleton } from "@asap/design";
import { getFullName } from "@asap/shared/utils/people";

const ACQUISITION_SKELETON_TILE_COUNT = 2;

const {
  period,
  referenceDate,
  selectedTeamMemberId,
  loading,
  error,
  refresh,
  viewModel,
  useMockData,
  lastSyncedAt,
  onKpiSynced,
} = useConsultantKpi();
const { isDeveloper, teamMember } = useUserMe();
const { managees, isManager } = useManagees();

const consultantName = computed(() => {
  const selectedId = selectedTeamMemberId.value;
  if (!selectedId) return "";
  if (teamMember.value?.id === selectedId) return getFullName(teamMember.value);
  const selectedManagee = managees.value.find((managee) => managee.id === selectedId);
  return selectedManagee ? getFullName(selectedManagee) : "";
});

const selectConsultant = (value: unknown) => {
  if (typeof value !== "string") return;
  selectedTeamMemberId.value = value;
};

const { isFeatureFlagEnabled } = useFeatureFlag();
const isConsultantKpiEnabled = isFeatureFlagEnabled("consultant-kpi");

watch(
  isConsultantKpiEnabled,
  (isEnabled) => {
    if (!isEnabled) {
      navigateTo("/dashboard/action-center");
    }
  },
  { immediate: true }
);

const ui = {
  body: "flex flex-col gap-6 overflow-auto p-6",
  header: "flex flex-col gap-3 border-b border-secondary-200 pb-6",
  toolbar: "flex w-full items-center gap-3",
  toolbarLeft: "flex items-center gap-3",
  toolbarFilter: "h-[34px] min-h-[34px] py-0",
  toolbarSync: "ml-auto shrink-0",
  identity: "flex flex-col gap-2",
  title: "text-3xl font-semibold leading-tight text-secondary-900",
  periodBadgeSkeleton: "h-6 w-28 rounded-full",
  titleSkeleton: "h-9 w-64",
  grid: "grid grid-cols-1 items-start gap-6 xl:grid-cols-[300px_1fr]",
  sidebar: "flex flex-col gap-6",
  main: "flex flex-col gap-6",
  funnelGrid: "grid grid-cols-1 items-start gap-6 lg:grid-cols-2",
  tileStack: "flex flex-col gap-2",
  errorState: "py-16",
};
</script>

<template>
  <PageContainer v-if="isConsultantKpiEnabled">
    <Title>📊 Performance</Title>
    <PageHeader fallback-icon="i-ph-chart-line-up" fallback-title="Performance" />

    <PageBody>
      <div :class="ui.body">
        <header :class="ui.header">
          <div :class="ui.toolbar">
            <div :class="ui.toolbarLeft">
              <ConsultantKpiPeriodToggle v-model="period" :disabled="loading" />
              <ConsultantKpiPeriodDatePicker v-model="referenceDate" :period="period" :disabled="loading" />
              <TeamMemberSelectV2
                v-if="selectedTeamMemberId"
                :value="selectedTeamMemberId"
                :multiple="false"
                :locked-scope="isDeveloper ? undefined : 'managees'"
                :roles="isDeveloper ? ['consultant_team_member'] : undefined"
                title="Consultant"
                :class="ui.toolbarFilter"
                :disabled="loading || (!isManager && !isDeveloper)"
                @update:value="selectConsultant"
              />
              <AwCheckbox v-if="isDeveloper" v-model="useMockData" label="Données mock" />
            </div>
            <ConsultantKpiSyncButton
              :class="ui.toolbarSync"
              :disabled="loading"
              :team-member-id="selectedTeamMemberId"
              :last-synced-at="lastSyncedAt"
              @synced="onKpiSynced"
            />
          </div>

          <div v-if="viewModel || loading || consultantName" :class="ui.identity">
            <div>
              <AwSkeleton v-if="loading" :class="ui.periodBadgeSkeleton" />
              <AwBadge v-else-if="viewModel" :label="viewModel.periodLabel" color="secondary" variant="outline" />
            </div>
            <h1 v-if="consultantName" :class="ui.title">{{ consultantName }}</h1>
            <AwSkeleton v-else-if="loading" :class="ui.titleSkeleton" />
          </div>
        </header>

        <AwEmptyState
          v-if="error && !viewModel"
          :class="ui.errorState"
          label="Impossible de charger les KPI"
          :description="error"
          :actions="[{ label: 'Réessayer', onClick: refresh }]"
        />

        <div v-else :class="ui.grid">
          <aside :class="ui.sidebar">
            <ConsultantKpiHighlightGrid :highlights="viewModel?.highlights" :loading="loading" />

            <ConsultantKpiProfitabilityFrame
              title="Rentabilité"
              :loading="loading"
              :display-value="viewModel?.profitability?.displayValue"
              :delta="viewModel?.profitability?.delta"
              :comparison="viewModel?.profitability?.comparison"
              :achievement-percent="viewModel?.profitability?.achievementPercent"
              :target="viewModel?.profitability?.target"
              :status="viewModel?.profitability?.status"
            >
              <ConsultantKpiPldEmptyState
                v-if="viewModel?.pldUnavailable"
                :height="120"
                :description="viewModel?.pldEmptyDescription"
              />
            </ConsultantKpiProfitabilityFrame>

            <ConsultantKpiLeverList :levers="viewModel?.levers" :loading="loading" />
          </aside>

          <div :class="ui.main">
            <ConsultantKpiTrajectoryFrame
              :kpis="viewModel?.trajectoryKpis"
              :trajectory="viewModel?.trajectory"
              :loading="loading"
            >
              <ConsultantKpiPldEmptyState
                v-if="viewModel?.pldUnavailable"
                :height="120"
                :description="viewModel?.pldEmptyDescription"
              />
              <LazyConsultantKpiChartsLineChart
                v-else-if="viewModel?.trajectory"
                :points="viewModel.trajectory.points"
                :target="viewModel.trajectory.targetValue"
              />
            </ConsultantKpiTrajectoryFrame>

            <ConsultantKpiProductivityFrame
              :kpis="viewModel?.productivityKpis"
              :trend-label="viewModel?.callsTrend.label"
              :loading="loading"
            >
              <LazyConsultantKpiChartsComboChart v-if="viewModel" :points="viewModel.callsTrend.points" />
            </ConsultantKpiProductivityFrame>

            <div :class="ui.funnelGrid">
              <ConsultantKpiCard title="Acquisition intérimaires" subtitle="Nouveaux et réactivations">
                <div :class="ui.tileStack">
                  <template v-if="loading">
                    <ConsultantKpiTile
                      v-for="index in ACQUISITION_SKELETON_TILE_COUNT"
                      :key="`acquisition-skeleton-${index}`"
                      loading
                      variant="plain"
                    />
                  </template>
                  <template v-else>
                    <ConsultantKpiTile
                      v-for="kpi in viewModel?.temporaryWorkerAcquisitionKpis"
                      :key="kpi.label"
                      v-bind="kpi"
                      variant="plain"
                    />
                  </template>
                </div>
              </ConsultantKpiCard>

              <ConsultantKpiRetentionPanel v-bind="viewModel?.retention" :loading="loading" />

              <ConsultantKpiFunnelFrame
                title="Acquisition clients"
                subtitle="New business"
                :block="viewModel?.clientAcquisition"
                :loading="loading"
              >
                <LazyConsultantKpiChartsFunnelChart v-if="viewModel" :steps="viewModel.clientAcquisition.steps" />
              </ConsultantKpiFunnelFrame>

              <ConsultantKpiFunnelFrame
                title="Fidélisation clients"
                subtitle="Approfondissement des comptes"
                :block="viewModel?.clientLoyalty"
                :loading="loading"
              >
                <LazyConsultantKpiChartsFunnelChart v-if="viewModel" :steps="viewModel.clientLoyalty.steps" />
              </ConsultantKpiFunnelFrame>
            </div>

            <ConsultantKpiPlanningEconomyFrame
              :title="viewModel?.planningEconomy.title"
              :ratio="viewModel?.planningEconomy.ratio"
              :occupancy="viewModel?.planningEconomy.occupancy"
              :kpis="viewModel?.planningEconomy.kpis"
              :loading="loading"
            />
          </div>
        </div>
      </div>
    </PageBody>
  </PageContainer>
</template>
